import AWS from "aws-sdk";
import React from "react";
import autoBind from "react-autobind";
import _ from "lodash";
import Modal from "react-responsive-modal";
import { Button } from "react-bootstrap";
import { getOrgId } from "../../actions/orgsActions";
import env from "../../constants/env";
import { refreshCredentials } from "../../utils/securityUtil";
import { fixFileNameForS3, generateUUID } from "../../utils/utils";

import {
	buildDefaultVideoOutputFileName,
	getSmartVideoFramingPresetOptions,
	normalizeVideoProcessingOptions,
	normalizeVideoOutputFileName,
	VIDEO_AUDIO_PRESET_OPTIONS,
	VIDEO_ORIENTATION_LANDSCAPE,
	VIDEO_ORIENTATION_MIXED,
	VIDEO_ORIENTATION_PORTRAIT,
	VIDEO_ROTATION_PRESET_OPTIONS,
	VIDEO_ORIENTATION_SQUARE,
	VIDEO_ORIENTATION_UNKNOWN,
	VIDEO_STYLE_FILTER_PRESET_OPTIONS,
	VIDEO_TONE_MAP_PRESET_OPTIONS,
	VIDEO_RESIZE_PRESET_OPTIONS,
	VIDEO_OUTPUT_EXTENSION
} from "../../utils/videoProcessingOptions";

const SERVER_PREVIEW_SLOW_WARNING_SECONDS = 45;
const SERVER_PREVIEW_VERY_SLOW_WARNING_SECONDS = 180;
const SERVER_PREVIEW_HARD_TIMEOUT_MS = 900000;
const SERVER_PREVIEW_POLL_INTERVAL_MS = 4000;
const SERVER_PREVIEW_URL_EXPIRES_SECONDS = 900;

class VideoProcessingOptionsModal extends React.PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		this._isMounted = false;
		this.previewObjectUrl = "";
		this.previewVideoRef = React.createRef();
		this.serverPreviewTimeout = null;
		this.serverPreviewRequestId = 0;
		this.serverPreviewSourceUploadPromise = null;
		this.serverPreviewElapsedInterval = null;
		this.serverPreviewRequestTimeout = null;
		this.serverPreviewPollTimeout = null;
		this.state = {
			options: this.getContextualOptions(props.initialOptions, props),
			overwriteVideo: this.getDefaultOverwriteVideo(props),
			continueInProgress: false,
			outputFileName: this.getDefaultOutputFileName(props),
			previewSourceUrl: "",
			sourceWidth: 0,
			sourceHeight: 0,
			stagedServerPreviewSource: null,
			serverPreviewProbe: null,
			serverPreviewAutoRender: this.getInitialServerPreviewAutoRender(props),
			serverPreviewPrimed: this.shouldPrimeServerPreview(props),
			serverPreviewUrl: "",
			serverPreviewMediaType: "",
			serverPreviewPreparingSource: false,
			serverPreviewLoading: false,
			serverPreviewError: "",
			serverPreviewUploadProgress: null,
			serverPreviewRequestStartedAt: 0,
			serverPreviewElapsedSeconds: 0,
			serverPreviewTimedOut: false,
			serverPreviewCancelled: false,
			serverPreviewOutputBucket: "",
			serverPreviewOutputKey: "",
			serverPreviewStatusBucket: "",
			serverPreviewStatusKey: ""
		};
	}

	componentDidMount() {
		this._isMounted = true;
		this.syncPreviewSource(this.props);
		this.scheduleServerPreview(this.props, this.state.options);
	}

	componentDidUpdate(prevProps, prevState) {
		let nextState = null;
		if (
			this.props.open &&
			(
				!prevProps.open ||
				prevProps.initialOptions !== this.props.initialOptions ||
				prevProps.fileName !== this.props.fileName ||
				prevProps.allowFileRename !== this.props.allowFileRename ||
				prevProps.defaultOutputFileName !== this.props.defaultOutputFileName ||
				prevProps.allowOverwriteOption !== this.props.allowOverwriteOption ||
				prevProps.defaultOverwrite !== this.props.defaultOverwrite ||
				prevProps.overwriteTargetPath !== this.props.overwriteTargetPath ||
				prevProps.orientationSummary !== this.props.orientationSummary
			)
		) {
			nextState = {
				options: this.getContextualOptions(this.props.initialOptions, this.props),
				overwriteVideo: this.getDefaultOverwriteVideo(this.props),
				outputFileName: this.getDefaultOutputFileName(this.props),
				sourceWidth: 0,
				sourceHeight: 0,
				serverPreviewProbe: null
			};
		}

		if (
			(!prevProps.open && this.props.open) ||
			prevProps.previewFile !== this.props.previewFile ||
			prevProps.serverPreviewSource !== this.props.serverPreviewSource ||
			prevProps.deferInitialServerPreview !== this.props.deferInitialServerPreview
		) {
			if (prevProps.previewFile !== this.props.previewFile && prevState.stagedServerPreviewSource) {
				this.cleanupStagedServerPreviewSource(prevState.stagedServerPreviewSource);
			}
			nextState = {
				...(nextState || {}),
				stagedServerPreviewSource: null,
				serverPreviewAutoRender: this.getInitialServerPreviewAutoRender(this.props),
				serverPreviewPrimed: this.shouldPrimeServerPreview(this.props),
				serverPreviewProbe: null,
				serverPreviewUrl: "",
				serverPreviewMediaType: "",
				serverPreviewPreparingSource: false,
				serverPreviewLoading: false,
				serverPreviewError: "",
				serverPreviewUploadProgress: null,
				serverPreviewRequestStartedAt: 0,
				serverPreviewElapsedSeconds: 0,
				serverPreviewTimedOut: false,
				serverPreviewCancelled: false,
				serverPreviewOutputBucket: "",
				serverPreviewOutputKey: "",
				serverPreviewStatusBucket: "",
				serverPreviewStatusKey: ""
			};
		}

		if (
			nextState &&
			(
				prevProps.previewUrl !== this.props.previewUrl ||
				prevProps.previewFile !== this.props.previewFile ||
				prevProps.open !== this.props.open
			)
		) {
			this.revokePreviewObjectUrl();
			nextState.previewSourceUrl = this.buildPreviewSourceUrl(this.props);
		}

		if (nextState) {
			this.clearServerPreviewTimeout();
			this.clearServerPreviewActivityTimers();
			this.clearServerPreviewPollingTimer();
			this.setState(nextState);
			return;
		}

		if (
			prevProps.previewUrl !== this.props.previewUrl ||
			prevProps.previewFile !== this.props.previewFile ||
			prevProps.open !== this.props.open
		) {
			this.syncPreviewSource(this.props);
		}

		if (
			prevState.options !== this.state.options &&
			this.canUseServerPreview(this.props) &&
			this.state.serverPreviewAutoRender !== true &&
			(this.state.serverPreviewUrl || this.state.serverPreviewError || this.state.serverPreviewLoading)
		) {
			this.clearServerPreviewResult({
				invalidateRequest: true
			});
			return;
		}

		if (
			prevState.options !== this.state.options ||
			prevProps.open !== this.props.open ||
			prevProps.serverPreviewSource !== this.props.serverPreviewSource ||
			prevState.serverPreviewPrimed !== this.state.serverPreviewPrimed ||
			prevState.serverPreviewAutoRender !== this.state.serverPreviewAutoRender
		) {
			this.scheduleServerPreview(this.props, this.state.options);
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		this.revokePreviewObjectUrl();
		this.clearServerPreviewTimeout();
		this.clearServerPreviewActivityTimers();
		this.clearServerPreviewPollingTimer();
		this.serverPreviewRequestId += 1;
		this.cleanupStagedServerPreviewSource();
	}

	getDefaultOutputFileName(props = this.props) {
		if (!props.allowFileRename) {
			return "";
		}
		if (this.getDefaultOverwriteVideo(props)) {
			return this.getOverwriteOutputFileName(props);
		}
		if (props.defaultOutputFileName) {
			return normalizeVideoOutputFileName(props.defaultOutputFileName, props.fileName, VIDEO_OUTPUT_EXTENSION);
		}
		return buildDefaultVideoOutputFileName(props.fileName, VIDEO_OUTPUT_EXTENSION);
	}

	getDefaultOverwriteVideo(props = this.props) {
		return !!(props && props.allowOverwriteOption === true && props.defaultOverwrite === true && `${props.overwriteTargetPath || ""}`.trim());
	}

	getOverwriteOutputFileName(props = this.props) {
		const overwriteTargetPath = `${props && props.overwriteTargetPath ? props.overwriteTargetPath : ""}`.trim();
		const overwriteName = overwriteTargetPath ? overwriteTargetPath.split("/").pop() : "";
		return normalizeVideoOutputFileName(overwriteName || (props && props.fileName) || "video.mp4", props && props.fileName, VIDEO_OUTPUT_EXTENSION);
	}

	getAvailableFramingOptions(props = this.props) {
		return getSmartVideoFramingPresetOptions(props.orientationSummary);
	}

	getContextualOptions(rawOptions = {}, props = this.props) {
		const normalizedOptions = normalizeVideoProcessingOptions(rawOptions);
		const allowedFramingPresetValues = this.getAvailableFramingOptions(props).map((option) => option.value);
		if (!allowedFramingPresetValues.includes(normalizedOptions.framingPreset)) {
			normalizedOptions.framingPreset = "source";
		}
		return normalizedOptions;
	}

	getFramingHelpText(props = this.props) {
		const orientationSummary = props && props.orientationSummary ? props.orientationSummary : {};
		const orientation = `${orientationSummary.orientation || ""}`.trim().toLowerCase();
		switch (orientation) {
			case VIDEO_ORIENTATION_PORTRAIT:
				return "Detected portrait upload. Blur-fill options target landscape outputs.";
			case VIDEO_ORIENTATION_LANDSCAPE:
				return "Detected landscape upload. Blur-fill options target portrait outputs.";
			case VIDEO_ORIENTATION_MIXED:
				return "Detected mixed video orientations. Showing both landscape and portrait blur-fill outputs.";
			case VIDEO_ORIENTATION_SQUARE:
				return "Detected square video. Showing both landscape and portrait blur-fill outputs.";
			case VIDEO_ORIENTATION_UNKNOWN:
			default:
				return "Showing both landscape and portrait blur-fill outputs when the source orientation is unknown.";
		}
	}

	revokePreviewObjectUrl() {
		if (this.previewObjectUrl && typeof window !== "undefined" && window.URL && typeof window.URL.revokeObjectURL === "function") {
			window.URL.revokeObjectURL(this.previewObjectUrl);
		}
		this.previewObjectUrl = "";
	}

	buildPreviewSourceUrl(props = this.props) {
		if (!props || props.open !== true) {
			return "";
		}

		const previewUrl = `${props && props.previewUrl ? props.previewUrl : ""}`.trim();
		if (previewUrl) {
			return previewUrl;
		}

		const previewFile = props ? props.previewFile : null;
		if (
			previewFile &&
			typeof window !== "undefined" &&
			window.URL &&
			typeof window.URL.createObjectURL === "function"
		) {
			this.previewObjectUrl = window.URL.createObjectURL(previewFile);
			return this.previewObjectUrl;
		}

		return "";
	}

	syncPreviewSource(props = this.props) {
		this.revokePreviewObjectUrl();
		const previewSourceUrl = this.buildPreviewSourceUrl(props);
		if (previewSourceUrl !== this.state.previewSourceUrl) {
			this.setState({
				previewSourceUrl
			});
		}
	}

	clearServerPreviewTimeout() {
		if (this.serverPreviewTimeout) {
			clearTimeout(this.serverPreviewTimeout);
			this.serverPreviewTimeout = null;
		}
	}

	clearServerPreviewActivityTimers() {
		if (this.serverPreviewElapsedInterval) {
			clearInterval(this.serverPreviewElapsedInterval);
			this.serverPreviewElapsedInterval = null;
		}
		if (this.serverPreviewRequestTimeout) {
			clearTimeout(this.serverPreviewRequestTimeout);
			this.serverPreviewRequestTimeout = null;
		}
	}

	clearServerPreviewPollingTimer() {
		if (this.serverPreviewPollTimeout) {
			clearTimeout(this.serverPreviewPollTimeout);
			this.serverPreviewPollTimeout = null;
		}
	}

	formatServerPreviewElapsed(seconds = 0) {
		const totalSeconds = Math.max(0, Number(seconds) || 0);
		const minutes = Math.floor(totalSeconds / 60);
		const remainingSeconds = totalSeconds % 60;
		return `${minutes}:${`${remainingSeconds}`.padStart(2, "0")}`;
	}

	formatServerPreviewError(error = null) {
		const message = `${error && error.message ? error.message : error || ""}`.trim();
		if (!message) {
			return "Processed preview failed. Try Render Preview again.";
		}
		if (message.includes("NoSuchKey")) {
			return "The temporary preview source could not be found. Click Render Preview to upload it again.";
		}
		if (message.includes("NetworkingError")) {
			return "The browser lost its connection while waiting for the server preview. Click Retry Preview.";
		}
		if (message.includes("Task timed out")) {
			return "The server preview ran longer than the Lambda timeout. Try lighter settings or a shorter trim.";
		}
		if (message.includes("The server preview response did not include a preview URL")) {
			return "The server finished without returning a preview URL. Click Retry Preview.";
		}
		if (message.includes("Preview render cancelled")) {
			return message;
		}
		return message;
	}

	startServerPreviewActivity(requestId, requestStartedAt = Date.now()) {
		this.clearServerPreviewActivityTimers();
		this.serverPreviewElapsedInterval = setInterval(() => {
			if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
				return;
			}
			const elapsedSeconds = Math.max(0, Math.floor((Date.now() - requestStartedAt) / 1000));
			this.setState((prevState) => (
				prevState.serverPreviewElapsedSeconds === elapsedSeconds
					? null
					: { serverPreviewElapsedSeconds: elapsedSeconds }
			));
		}, 1000);

		this.serverPreviewRequestTimeout = setTimeout(() => {
			if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
				return;
			}
			const elapsedSeconds = Math.max(0, Math.floor((Date.now() - requestStartedAt) / 1000));
			this.serverPreviewRequestId += 1;
			this.clearServerPreviewActivityTimers();
			this.clearServerPreviewPollingTimer();
			this.setState({
				serverPreviewPreparingSource: false,
				serverPreviewLoading: false,
				serverPreviewMediaType: "",
				serverPreviewError: `Processed preview has still not finished after ${this.formatServerPreviewElapsed(elapsedSeconds)}. Retry Preview will first check for a completed result before queueing a new render.`,
				serverPreviewElapsedSeconds: elapsedSeconds,
				serverPreviewTimedOut: true
			});
		}, SERVER_PREVIEW_HARD_TIMEOUT_MS);
	}

	clearServerPreviewResult({ invalidateRequest = false } = {}) {
		this.clearServerPreviewTimeout();
		this.clearServerPreviewActivityTimers();
		this.clearServerPreviewPollingTimer();
		if (invalidateRequest) {
			this.serverPreviewRequestId += 1;
		}
		this.setState({
			serverPreviewProbe: null,
			serverPreviewUrl: "",
			serverPreviewMediaType: "",
			serverPreviewPreparingSource: false,
			serverPreviewLoading: false,
			serverPreviewError: "",
			serverPreviewUploadProgress: null,
			serverPreviewRequestStartedAt: 0,
			serverPreviewElapsedSeconds: 0,
			serverPreviewTimedOut: false,
			serverPreviewCancelled: false,
			serverPreviewOutputBucket: "",
			serverPreviewOutputKey: "",
			serverPreviewStatusBucket: "",
			serverPreviewStatusKey: ""
		});
	}

	getResolvedServerPreviewSource(props = this.props, state = this.state) {
		const providedSource = props && props.serverPreviewSource ? props.serverPreviewSource : null;
		if (providedSource && providedSource.bucket && providedSource.key) {
			return providedSource;
		}

		const stagedSource = state && state.stagedServerPreviewSource ? state.stagedServerPreviewSource : null;
		if (stagedSource && stagedSource.bucket && stagedSource.key) {
			return stagedSource;
		}

		return null;
	}

	canStagePreviewFileForServerPreview(props = this.props, state = this.state) {
		const previewFile = props ? props.previewFile : null;
		if (!props || props.open !== true) {
			return false;
		}
		if (this.getResolvedServerPreviewSource(props, state)) {
			return false;
		}
		return !!previewFile;
	}

	canUseServerPreview(props = this.props, state = this.state) {
		if (!props || props.open !== true) {
			return false;
		}

		const source = this.getResolvedServerPreviewSource(props, state);
		if (source && source.bucket && source.key) {
			return true;
		}

		return this.canStagePreviewFileForServerPreview(props, state);
	}

	getInitialServerPreviewAutoRender(props = this.props) {
		if (props && props.deferInitialServerPreview === true) {
			return false;
		}
		if (this.canStagePreviewFileForServerPreview(props, {
			stagedServerPreviewSource: null
		})) {
			return false;
		}
		return true;
	}

	shouldPrimeServerPreview(props = this.props) {
		return this.getInitialServerPreviewAutoRender(props);
	}

	shouldRequestServerPreview(props = this.props, state = this.state) {
		return this.canUseServerPreview(props, state) && !!(state && state.serverPreviewPrimed) && !!(state && state.serverPreviewAutoRender);
	}

	looksLikeHdrProbe(probe = null) {
		const target = probe && typeof probe === "object" ? probe : {};
		return target.colorTransfer === "arib-std-b67" ||
			target.colorPrimaries === "bt2020" ||
			target.colorSpace === "bt2020nc" ||
			`${target.pixelFormat || ""}`.indexOf("10") >= 0;
	}

	getHdrVisibilityStatus(props = this.props, state = this.state) {
		if (!this.canUseServerPreview(props, state)) {
			return "unknown";
		}

		const probe = state && state.serverPreviewProbe && typeof state.serverPreviewProbe === "object"
			? state.serverPreviewProbe
			: null;
		if (!probe) {
			return "unknown";
		}

		return this.looksLikeHdrProbe(probe) ? "hdr" : "sdr";
	}

	getMediaConverterFunctionName() {
		if (env && env.lambda && env.lambda.mediaConverter) {
			return env.lambda.mediaConverter;
		}
		const stage = `${env && env.stage ? env.stage : "dev"}`.trim() || "dev";
		return `ambifi-media-converter-lambda-${stage}`;
	}

	getServerPreviewBucket() {
		return `${_.get(env, "s3.converterBucket", "")}`.trim();
	}

	getServerPreviewSourceVersionToken(props = this.props, state = this.state) {
		const source = this.getResolvedServerPreviewSource(props, state);
		if (!source || typeof source !== "object") {
			return "";
		}

		const candidates = [
			source.sourceVersionToken,
			source.versionId,
			source.etag,
			source.revision,
			source.lastModified
		];
		for (const candidate of candidates) {
			const normalized = `${candidate || ""}`.trim();
			if (normalized) {
				return normalized
					.replace(/[^a-zA-Z0-9._-]+/g, "-")
					.replace(/-+/g, "-")
					.replace(/^[-_.]+|[-_.]+$/g, "");
			}
		}

		return "";
	}

	getServerPreviewBaseKey(key = "") {
		const normalizedKey = `${key || ""}`.trim().replace(/^\/+/, "");
		if (!normalizedKey) {
			return "";
		}
		const lastSlashIndex = normalizedKey.lastIndexOf("/");
		const lastDotIndex = normalizedKey.lastIndexOf(".");
		if (lastDotIndex > lastSlashIndex) {
			return normalizedKey.substring(0, lastDotIndex);
		}
		return normalizedKey;
	}

	buildServerPreviewOutputKey(sourceKey = "", options = this.state.options, sourceVersionToken = "") {
		const sourceBaseKey = this.getServerPreviewBaseKey(sourceKey);
		if (!sourceBaseKey) {
			return "";
		}

		const sourceStem = sourceBaseKey.split("/").pop() || "video";
		const normalizedOptions = this.getContextualOptions(options, this.props);
		const framingPreset = `${normalizedOptions.framingPreset || "source"}`.trim() || "source";
		const resizePreset = `${normalizedOptions.resizePreset || "source"}`.trim() || "source";
		const toneMapPreset = `${normalizedOptions.hdrToneMapPreset || "off"}`.trim() || "off";
		const styleFilterPreset = `${normalizedOptions.styleFilterPreset || "none"}`.trim() || "none";
		const rotationPreset = `${normalizedOptions.rotationPreset || "source"}`.trim() || "source";
		const audioPreset = `${normalizedOptions.audioPreset || "keep"}`.trim() || "keep";
		const trimStartSeconds = Math.max(0, Math.round((Number(normalizedOptions.trimStartSeconds) || 0) * 100) / 100);
		const trimDurationSeconds = Math.max(0, Math.round((Number(normalizedOptions.trimDurationSeconds) || 0) * 100) / 100);
		const sourceVersionSuffix = sourceVersionToken ? `-src${sourceVersionToken}` : "";
		return `video-preview-temp/${sourceBaseKey}/${sourceStem}-${framingPreset}-${resizePreset}-${toneMapPreset}-${styleFilterPreset}-${rotationPreset}-${audioPreset}-ts${trimStartSeconds}-td${trimDurationSeconds}${sourceVersionSuffix}.mp4`;
	}

	buildServerPreviewStatusKey(outputKey = "") {
		const normalizedOutputKey = `${outputKey || ""}`.trim().replace(/^\/+/, "");
		return normalizedOutputKey ? `${normalizedOutputKey}.status.json` : "";
	}

	async getServerPreviewStatusDocument(bucket = "", key = "") {
		const normalizedBucket = `${bucket || ""}`.trim();
		const normalizedKey = `${key || ""}`.trim().replace(/^\/+/, "");
		if (!normalizedBucket || !normalizedKey) {
			return null;
		}

		await refreshCredentials();
		const s3 = new AWS.S3({
			useDualStack: true,
			params: {
				Bucket: normalizedBucket,
				ResponseContentType: "application/json",
				ResponseCacheControl: "no-cache"
			}
		});
		try {
			const data = await s3.getObject({ Key: normalizedKey }).promise();
			return JSON.parse(`${data && data.Body ? data.Body.toString() : "{}"}`.trim() || "{}");
		} catch (error) {
			if (error && (error.code === "NoSuchKey" || error.code === "NotFound")) {
				return null;
			}
			throw error;
		}
	}

	async doesServerPreviewObjectExist(bucket = "", key = "") {
		const normalizedBucket = `${bucket || ""}`.trim();
		const normalizedKey = `${key || ""}`.trim().replace(/^\/+/, "");
		if (!normalizedBucket || !normalizedKey) {
			return false;
		}

		await refreshCredentials();
		const s3 = new AWS.S3({ useDualStack: true, params: { Bucket: normalizedBucket } });
		try {
			await s3.headObject({ Key: normalizedKey }).promise();
			return true;
		} catch (error) {
			if (error && (error.code === "NotFound" || error.code === "NoSuchKey")) {
				return false;
			}
			throw error;
		}
	}

	async getSignedServerPreviewUrl(bucket = "", key = "", expiresSeconds = SERVER_PREVIEW_URL_EXPIRES_SECONDS) {
		const normalizedBucket = `${bucket || ""}`.trim();
		const normalizedKey = `${key || ""}`.trim().replace(/^\/+/, "");
		if (!normalizedBucket || !normalizedKey) {
			return "";
		}

		await refreshCredentials();
		const s3 = new AWS.S3({ useDualStack: true });
		return s3.getSignedUrl("getObject", {
			Bucket: normalizedBucket,
			Key: normalizedKey,
			Expires: expiresSeconds
		});
	}

	async tryApplyExistingServerPreviewResult({
		requestId,
		outputBucket = "",
		outputKey = "",
		statusBucket = "",
		statusKey = "",
		allowResumePending = false,
		forceRerender = false
	} = {}) {
		if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
			return { handled: true, ready: false, pending: false };
		}

		const normalizedOutputBucket = `${outputBucket || ""}`.trim();
		const normalizedOutputKey = `${outputKey || ""}`.trim().replace(/^\/+/, "");
		const normalizedStatusBucket = `${statusBucket || normalizedOutputBucket}`.trim();
		const normalizedStatusKey = `${statusKey || this.buildServerPreviewStatusKey(normalizedOutputKey)}`.trim().replace(/^\/+/, "");
		if (!normalizedOutputBucket || !normalizedOutputKey) {
			return { handled: false, ready: false, pending: false };
		}

		try {
			const statusDocument = normalizedStatusKey
				? await this.getServerPreviewStatusDocument(normalizedStatusBucket, normalizedStatusKey)
				: null;
			if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
				return { handled: true, ready: false, pending: false };
			}

			const statusState = `${_.get(statusDocument, "state", "")}`.trim().toLowerCase();
			const statusStartedAt = new Date(_.get(statusDocument, "startedAt", "")).getTime();
			const isStaleRunningStatus = statusState === "running" &&
				Number.isFinite(statusStartedAt) &&
				(Date.now() - statusStartedAt) > (SERVER_PREVIEW_HARD_TIMEOUT_MS + 30000);

			if (statusState === "complete" || statusState === "success" || await this.doesServerPreviewObjectExist(normalizedOutputBucket, normalizedOutputKey)) {
				const signedUrl = await this.getSignedServerPreviewUrl(normalizedOutputBucket, normalizedOutputKey);
				if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
					return { handled: true, ready: false, pending: false };
				}
				this.clearServerPreviewActivityTimers();
				this.clearServerPreviewPollingTimer();
				this.setState({
					serverPreviewProbe: statusDocument && typeof statusDocument.probe === "object" ? statusDocument.probe : null,
					serverPreviewUrl: `${signedUrl || ""}`,
					serverPreviewMediaType: `${_.get(statusDocument, "mediaType", "video") || "video"}`.trim().toLowerCase(),
					serverPreviewPreparingSource: false,
					serverPreviewLoading: false,
					serverPreviewError: "",
					serverPreviewUploadProgress: null,
					serverPreviewTimedOut: false,
					serverPreviewCancelled: false,
					serverPreviewOutputBucket: normalizedOutputBucket,
					serverPreviewOutputKey: normalizedOutputKey,
					serverPreviewStatusBucket: normalizedStatusBucket,
					serverPreviewStatusKey: normalizedStatusKey
				});
				return { handled: true, ready: true, pending: false };
			}

			if (allowResumePending && statusState === "running" && !isStaleRunningStatus) {
				this.setState({
					serverPreviewPreparingSource: false,
					serverPreviewLoading: true,
					serverPreviewError: "",
					serverPreviewTimedOut: false,
					serverPreviewCancelled: false,
					serverPreviewOutputBucket: normalizedOutputBucket,
					serverPreviewOutputKey: normalizedOutputKey,
					serverPreviewStatusBucket: normalizedStatusBucket,
					serverPreviewStatusKey: normalizedStatusKey
				});
				return {
					handled: true,
					ready: false,
					pending: true,
					message: `${_.get(statusDocument, "message", "")}`.trim()
				};
			}

			if (!forceRerender && statusState === "failed") {
				const errorMessage = this.formatServerPreviewError(_.get(statusDocument, "errorMessage", "Processed preview failed."));
				this.clearServerPreviewActivityTimers();
				this.clearServerPreviewPollingTimer();
				this.setState({
					serverPreviewPreparingSource: false,
					serverPreviewLoading: false,
					serverPreviewMediaType: "",
					serverPreviewError: errorMessage,
					serverPreviewTimedOut: false,
					serverPreviewCancelled: false,
					serverPreviewOutputBucket: normalizedOutputBucket,
					serverPreviewOutputKey: normalizedOutputKey,
					serverPreviewStatusBucket: normalizedStatusBucket,
					serverPreviewStatusKey: normalizedStatusKey
				});
				return { handled: true, ready: false, pending: false };
			}
		} catch (error) {
			if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
				return { handled: true, ready: false, pending: false };
			}
			console.warn("Unable to inspect existing server preview result", error);
		}

		return { handled: false, ready: false, pending: false };
	}

	scheduleServerPreviewPoll({
		requestId,
		outputBucket = "",
		outputKey = "",
		statusBucket = "",
		statusKey = ""
	} = {}) {
		this.clearServerPreviewPollingTimer();
		if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
			return;
		}
		this.serverPreviewPollTimeout = setTimeout(() => {
			this.serverPreviewPollTimeout = null;
			this.pollServerPreviewResult({
				requestId,
				outputBucket,
				outputKey,
				statusBucket,
				statusKey
			});
		}, SERVER_PREVIEW_POLL_INTERVAL_MS);
	}

	async pollServerPreviewResult({
		requestId,
		outputBucket = "",
		outputKey = "",
		statusBucket = "",
		statusKey = ""
	} = {}) {
		if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
			return;
		}

		try {
			const result = await this.tryApplyExistingServerPreviewResult({
				requestId,
				outputBucket,
				outputKey,
				statusBucket,
				statusKey,
				allowResumePending: true,
				forceRerender: false
			});
			if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
				return;
			}
			if (result.ready === true) {
				return;
			}
			if (result.handled === true && result.pending !== true) {
				return;
			}
		} catch (error) {
			if (!this._isMounted || requestId !== this.serverPreviewRequestId) {
				return;
			}
			console.warn("Unable to poll processed preview status", error);
		}

		if (requestId === this.serverPreviewRequestId) {
			this.scheduleServerPreviewPoll({
				requestId,
				outputBucket,
				outputKey,
				statusBucket,
				statusKey
			});
		}
	}

	scheduleServerPreview(props = this.props, options = this.state.options) {
		this.clearServerPreviewTimeout();
		if (!this.canUseServerPreview(props, this.state)) {
			this.clearServerPreviewActivityTimers();
			this.clearServerPreviewPollingTimer();
			if (this.state.serverPreviewUrl || this.state.serverPreviewLoading || this.state.serverPreviewError) {
				this.setState({
					serverPreviewUrl: "",
					serverPreviewMediaType: "",
					serverPreviewPreparingSource: false,
					serverPreviewLoading: false,
					serverPreviewError: ""
				});
			}
			return;
		}

		if (!this.shouldRequestServerPreview(props)) {
			return;
		}

		this.serverPreviewTimeout = setTimeout(() => {
			this.serverPreviewTimeout = null;
			this.requestServerPreview(props, options);
		}, 700);
	}

	buildStagedServerPreviewSource(props = this.props) {
		const previewFile = props ? props.previewFile : null;
		const orgId = `${getOrgId() || ""}`.trim();
		if (!previewFile || !orgId) {
			return null;
		}

		const fallbackExtension = `${previewFile && previewFile.type ? previewFile.type.split("/").pop() : ""}`.trim().toLowerCase() || "mp4";
		const fallbackName = `video-preview-${generateUUID()}.${fallbackExtension}`;
		const normalizedFileName = fixFileNameForS3(previewFile && previewFile.name ? previewFile.name : fallbackName) || fallbackName;
		const bucket = env && env.s3 ? env.s3.assetsBucket : "";
		if (!bucket) {
			return null;
		}

		return {
			bucket,
			key: `private/${orgId}/_video-processing-preview/${generateUUID()}/${normalizedFileName}`
		};
	}

	cleanupStagedServerPreviewSource(source = this.state.stagedServerPreviewSource) {
		const targetSource = source && typeof source === "object" ? source : null;
		if (!targetSource || !targetSource.bucket || !targetSource.key) {
			return;
		}

		refreshCredentials().then(() => {
			const s3 = new AWS.S3({ useDualStack: true, params: { Bucket: targetSource.bucket } });
			return s3.deleteObject({ Key: targetSource.key }).promise();
		}).catch((error) => {
			console.warn("Unable to clean up staged server preview source", error);
		});
	}

	async ensureServerPreviewSource(props = this.props, { credentialsReady = false } = {}) {
		const existingSource = this.getResolvedServerPreviewSource(props, this.state);
		if (existingSource && existingSource.bucket && existingSource.key) {
			return existingSource;
		}
		if (!this.canStagePreviewFileForServerPreview(props, this.state)) {
			return null;
		}
		if (this.serverPreviewSourceUploadPromise) {
			return this.serverPreviewSourceUploadPromise;
		}

		const previewFile = props ? props.previewFile : null;
		const stagedSource = this.buildStagedServerPreviewSource(props);
		if (!previewFile || !stagedSource || !stagedSource.bucket || !stagedSource.key) {
			throw new Error("Unable to prepare a processed preview for this local upload.");
		}

		this.serverPreviewSourceUploadPromise = (async () => {
			if (!credentialsReady) {
				await refreshCredentials();
			}

			this.setState({
				serverPreviewPreparingSource: true,
				serverPreviewError: "",
				serverPreviewUploadProgress: 0
			});
			const s3 = new AWS.S3({ useDualStack: true, params: { Bucket: stagedSource.bucket } });
			const upload = s3.upload({
				Key: stagedSource.key,
				Body: previewFile,
				ContentType: `${previewFile.type || "video/mp4"}`.trim() || "video/mp4",
				Metadata: {
					"skip-transcription": "true",
					"temporary-video-processing-preview": "true"
				}
			});
			upload.on("httpUploadProgress", (progressEvent = {}) => {
				if (!this._isMounted) {
					return;
				}
				const total = Number(progressEvent.total) || 0;
				const loaded = Number(progressEvent.loaded) || 0;
				const nextProgress = total > 0 ? Math.max(0, Math.min(1, loaded / total)) : null;
				this.setState((prevState) => (
					prevState.serverPreviewUploadProgress === nextProgress
						? null
						: { serverPreviewUploadProgress: nextProgress }
				));
			});
			await upload.promise();
			if (!this._isMounted) {
				this.cleanupStagedServerPreviewSource(stagedSource);
				return stagedSource;
			}
			this.setState({
				stagedServerPreviewSource: stagedSource,
				serverPreviewPreparingSource: false,
				serverPreviewUploadProgress: 1
			});
			return stagedSource;
		})().finally(() => {
			this.serverPreviewSourceUploadPromise = null;
		});

		return this.serverPreviewSourceUploadPromise;
	}

	async requestServerPreview(props = this.props, options = this.state.options, { force = false } = {}) {
		if (!this.canUseServerPreview(props, this.state)) {
			return;
		}

		if (!force && !this.shouldRequestServerPreview(props)) {
			return;
		}

		const requestId = ++this.serverPreviewRequestId;
		const requestStartedAt = Date.now();
		this.setState({
			serverPreviewUrl: "",
			serverPreviewMediaType: "",
			serverPreviewPreparingSource: false,
			serverPreviewLoading: true,
			serverPreviewError: "",
			serverPreviewUploadProgress: null,
			serverPreviewRequestStartedAt: requestStartedAt,
			serverPreviewElapsedSeconds: 0,
			serverPreviewTimedOut: false,
			serverPreviewCancelled: false,
			serverPreviewOutputBucket: "",
			serverPreviewOutputKey: "",
			serverPreviewStatusBucket: "",
			serverPreviewStatusKey: ""
		});
		this.startServerPreviewActivity(requestId, requestStartedAt);

		try {
			const source = await this.ensureServerPreviewSource(props, {
				credentialsReady: false
			});
			if (requestId !== this.serverPreviewRequestId) {
				return;
			}
			if (!source || !source.bucket || !source.key) {
				throw new Error("Server preview source is unavailable.");
			}

			const previewBucket = this.getServerPreviewBucket() || source.bucket;
			const sourceVersionToken = this.getServerPreviewSourceVersionToken(props, {
				...this.state,
				stagedServerPreviewSource: this.state.stagedServerPreviewSource || source
			});
			const outputKey = this.buildServerPreviewOutputKey(source.key, options, sourceVersionToken);
			const statusBucket = previewBucket;
			const statusKey = this.buildServerPreviewStatusKey(outputKey);
			if (!outputKey || !statusKey) {
				throw new Error("Unable to determine the processed preview output path.");
			}

			this.setState({
				serverPreviewOutputBucket: previewBucket,
				serverPreviewOutputKey: outputKey,
				serverPreviewStatusBucket: statusBucket,
				serverPreviewStatusKey: statusKey
			});

			const existingResult = await this.tryApplyExistingServerPreviewResult({
				requestId,
				outputBucket: previewBucket,
				outputKey,
				statusBucket,
				statusKey,
				allowResumePending: true,
				forceRerender: force
			});
			if (requestId !== this.serverPreviewRequestId) {
				return;
			}
			if (existingResult.ready === true) {
				return;
			}
			if (existingResult.pending === true) {
				this.pollServerPreviewResult({
					requestId,
					outputBucket: previewBucket,
					outputKey,
					statusBucket,
					statusKey
				});
				return;
			}

			await refreshCredentials();
			const lambda = new AWS.Lambda({ region: (env && env.aws && env.aws.defaultRegion) || "us-east-1", apiVersion: "2015-03-31" });
			const params = {
				FunctionName: this.getMediaConverterFunctionName(),
				InvocationType: "Event",
				LogType: "None",
				Payload: JSON.stringify({
					eventName: "renderVideoPreviewVideo",
					params: {
						bucket: source.bucket,
						key: source.key,
						processingOptions: this.getContextualOptions(options, props),
						previewBucket,
						previewKey: outputKey,
						statusBucket,
						statusKey
					}
				})
			};
			const data = await lambda.invoke(params).promise();
			if (requestId !== this.serverPreviewRequestId) {
				return;
			}
			if (data && Number(data.StatusCode) >= 300) {
				throw new Error(`Unable to queue server preview render (status ${data.StatusCode}).`);
			}

			this.pollServerPreviewResult({
				requestId,
				outputBucket: previewBucket,
				outputKey,
				statusBucket,
				statusKey
			});
		} catch (error) {
			if (requestId !== this.serverPreviewRequestId) {
				return;
			}
			console.error("Unable to refresh credentials or prepare source for server video preview", error);
			this.clearServerPreviewActivityTimers();
			this.setState({
				serverPreviewLoading: false,
				serverPreviewPreparingSource: false,
				serverPreviewMediaType: "",
				serverPreviewError: this.formatServerPreviewError(error)
			});
		}
	}

	handleSelectChange(event) {
		const { name, value } = event.target;
		this.setState((prevState) => ({
			options: this.getContextualOptions({
				...prevState.options,
				[name]: value
			}, this.props)
		}));
	}

	handleCheckboxChange(event) {
		const { name, checked } = event.target;
		this.setState((prevState) => ({
			options: this.getContextualOptions({
				...prevState.options,
				[name]: checked
			}, this.props)
		}));
	}

	handleNumberOptionChange(event) {
		const { name, value } = event.target;
		const parsed = Number.parseFloat(`${value || ""}`);
		this.setState((prevState) => ({
			options: this.getContextualOptions({
				...prevState.options,
				[name]: Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : 0
			}, this.props)
		}));
	}

	handleOutputFileNameChange(event) {
		this.setState({
			outputFileName: event && event.target ? event.target.value || "" : ""
		});
	}

	handleOverwriteVideoChange(event) {
		const overwriteVideo = !!(event && event.target && event.target.checked);
		this.setState({
			overwriteVideo,
			outputFileName: overwriteVideo
				? this.getOverwriteOutputFileName()
				: this.getDefaultOutputFileName({
					...this.props,
					defaultOverwrite: false
				})
		});
	}

	handlePreviewLoadedMetadata(event) {
		const video = event && event.target ? event.target : null;
		const sourceWidth = Number(video && video.videoWidth) || 0;
		const sourceHeight = Number(video && video.videoHeight) || 0;
		if (sourceWidth !== this.state.sourceWidth || sourceHeight !== this.state.sourceHeight) {
			this.setState({
				sourceWidth,
				sourceHeight
			});
		}
	}

	getCurrentPreviewScrubTime() {
		const video = this.previewVideoRef && this.previewVideoRef.current ? this.previewVideoRef.current : null;
		if (!video) {
			return null;
		}

		const currentTime = Number(video.currentTime);
		if (!Number.isFinite(currentTime) || currentTime < 0) {
			return null;
		}

		const isShowingServerVideo = this.canUseServerPreview(this.props) &&
			this.state.serverPreviewMediaType === "video" &&
			!!this.state.serverPreviewUrl;
		const sourceOffset = isShowingServerVideo
			? (Number(this.state.options && this.state.options.trimStartSeconds) || 0)
			: 0;
		return Math.round((currentTime + sourceOffset) * 100) / 100;
	}

	handleCaptureTrimStartFromPreview() {
		const currentScrubTime = this.getCurrentPreviewScrubTime();
		if (currentScrubTime === null) {
			return;
		}

		this.setState((prevState) => {
			const currentOptions = this.getContextualOptions(prevState.options, this.props);
			const previousStart = Number(currentOptions.trimStartSeconds) || 0;
			const previousDuration = Number(currentOptions.trimDurationSeconds) || 0;
			const previousEndAbsolute = previousDuration > 0 ? previousStart + previousDuration : 0;
			const nextOptions = {
				...currentOptions,
				trimStartSeconds: currentScrubTime
			};
			if (previousDuration > 0) {
				nextOptions.trimDurationSeconds = previousEndAbsolute > currentScrubTime
					? Math.round((previousEndAbsolute - currentScrubTime) * 100) / 100
					: 0;
			}
			return {
				options: this.getContextualOptions(nextOptions, this.props)
			};
		});
	}

	handleCaptureTrimEndFromPreview() {
		const currentScrubTime = this.getCurrentPreviewScrubTime();
		if (currentScrubTime === null) {
			return;
		}

		this.setState((prevState) => {
			const currentOptions = this.getContextualOptions(prevState.options, this.props);
			const trimStartSeconds = Number(currentOptions.trimStartSeconds) || 0;
			return {
				options: this.getContextualOptions({
					...currentOptions,
					trimDurationSeconds: currentScrubTime > trimStartSeconds
						? Math.round((currentScrubTime - trimStartSeconds) * 100) / 100
						: 0
				}, this.props)
			};
		});
	}

	parsePresetDimensions(presetValue = "") {
		const match = `${presetValue || ""}`.match(/(\d+)x(\d+)/);
		if (!match) {
			return null;
		}
		return {
			width: Number(match[1]) || 0,
			height: Number(match[2]) || 0
		};
	}

	getSourceAspectRatio() {
		const sourceDimensions = this.getEffectiveSourceDimensions();
		if (sourceDimensions.width > 0 && sourceDimensions.height > 0) {
			return sourceDimensions.width / sourceDimensions.height;
		}

		const orientationSummary = this.props && this.props.orientationSummary ? this.props.orientationSummary : {};
		const orientation = `${orientationSummary.orientation || ""}`.trim().toLowerCase();
		switch (orientation) {
			case VIDEO_ORIENTATION_PORTRAIT:
				return 9 / 16;
			case VIDEO_ORIENTATION_SQUARE:
				return 1;
			case VIDEO_ORIENTATION_LANDSCAPE:
			case VIDEO_ORIENTATION_MIXED:
			case VIDEO_ORIENTATION_UNKNOWN:
			default:
				return 16 / 9;
		}
	}

	doesPreviewRotationSwapDimensions(options = this.state.options) {
		const rotationPreset = `${options && options.rotationPreset ? options.rotationPreset : "source"}`.trim();
		return rotationPreset === "rotate-90-cw" || rotationPreset === "rotate-90-ccw";
	}

	getEffectiveSourceDimensions(options = this.state.options) {
		const { sourceWidth, sourceHeight } = this.state;
		if (sourceWidth <= 0 || sourceHeight <= 0) {
			return {
				width: sourceWidth,
				height: sourceHeight
			};
		}

		return this.doesPreviewRotationSwapDimensions(options)
			? { width: sourceHeight, height: sourceWidth }
			: { width: sourceWidth, height: sourceHeight };
	}

	getPreviewFrameDimensions(options = this.state.options) {
		const framingDimensions = this.parsePresetDimensions(options && options.framingPreset);
		if (framingDimensions) {
			return framingDimensions;
		}

		const resizeDimensions = this.parsePresetDimensions(options && options.resizePreset);
		if (resizeDimensions) {
			return resizeDimensions;
		}

		const effectiveSourceDimensions = this.getEffectiveSourceDimensions(options);
		if (effectiveSourceDimensions.width > 0 && effectiveSourceDimensions.height > 0) {
			return {
				width: effectiveSourceDimensions.width,
				height: effectiveSourceDimensions.height
			};
		}

		const fallbackAspectRatio = this.getSourceAspectRatio();
		return fallbackAspectRatio >= 1
			? { width: 1600, height: Math.round(1600 / fallbackAspectRatio) }
			: { width: Math.round(900 * fallbackAspectRatio), height: 900 };
	}

	getPreviewFilter(options = this.state.options) {
		const filters = [];
		switch (`${options && options.hdrToneMapPreset ? options.hdrToneMapPreset : "off"}`.trim()) {
			case "auto-hable":
				filters.push("contrast(0.98)", "saturate(0.96)", "brightness(0.99)");
				break;
			case "auto-mobius":
				filters.push("contrast(1.02)", "saturate(1.03)");
				break;
			case "auto-reinhard":
				filters.push("brightness(1.03)", "contrast(0.95)", "saturate(0.95)");
				break;
			case "auto-clip":
				filters.push("contrast(1.06)", "brightness(1.02)");
				break;
			case "off":
			default:
				break;
		}

		switch (`${options && options.styleFilterPreset ? options.styleFilterPreset : "none"}`.trim()) {
			case "black-white":
				filters.push("grayscale(1)", "contrast(1.04)");
				break;
			case "sepia":
				filters.push("sepia(0.92)", "saturate(1.12)", "contrast(1.02)");
				break;
			case "high-contrast":
				filters.push("contrast(1.18)", "saturate(1.02)");
				break;
			case "vivid":
				filters.push("saturate(1.22)", "contrast(1.08)", "brightness(1.02)");
				break;
			case "none":
			default:
				break;
		}

		return filters.length > 0 ? filters.join(" ") : "none";
	}

	getPreviewTransform(options = this.state.options, { includeBlurScale = false } = {}) {
		const transforms = [];
		switch (`${options && options.rotationPreset ? options.rotationPreset : "source"}`.trim()) {
			case "rotate-90-cw":
				transforms.push("rotate(90deg)");
				break;
			case "rotate-90-ccw":
				transforms.push("rotate(-90deg)");
				break;
			case "rotate-180":
				transforms.push("rotate(180deg)");
				break;
			case "flip-horizontal":
				transforms.push("scaleX(-1)");
				break;
			case "flip-vertical":
				transforms.push("scaleY(-1)");
				break;
			case "source":
			default:
				break;
		}
		if (includeBlurScale) {
			transforms.push("scale(1.12)");
		}
		return transforms.length > 0 ? transforms.join(" ") : "none";
	}

	getPreviewObjectFit(options = this.state.options) {
		const framingPreset = `${options && options.framingPreset ? options.framingPreset : ""}`.trim().toLowerCase();
		if (framingPreset.startsWith("crop-fill-")) {
			return "cover";
		}
		return "contain";
	}

	getPreviewNotes(options = this.state.options) {
		const notes = [];
		const framingPreset = `${options && options.framingPreset ? options.framingPreset : ""}`.trim();
		if (framingPreset.startsWith("blur-fill-")) {
			notes.push("Live preview shows approximate blur-fill framing.");
		}
		if (framingPreset.startsWith("crop-fill-")) {
			notes.push("Center crop fills the selected canvas and may trim edges from the source frame.");
		}
		if (framingPreset.startsWith("pad-black-")) {
			notes.push("Pad mode preserves the full frame and adds black bars when needed.");
		}
		if (`${options && options.resizePreset ? options.resizePreset : ""}` !== "source") {
			notes.push("Frame shape reflects the selected output target.");
		}
		if (`${options && options.hdrToneMapPreset ? options.hdrToneMapPreset : "off"}` !== "off") {
			notes.push("HDR tone-map preview is approximate; final color comes from server conversion.");
		}
		if (`${options && options.styleFilterPreset ? options.styleFilterPreset : "none"}` !== "none") {
			notes.push("Style filter preview is approximate locally; server preview reflects the final ffmpeg output.");
		}
		if (`${options && options.rotationPreset ? options.rotationPreset : "source"}` !== "source") {
			notes.push("Rotation and flip changes are applied before resize and framing.");
		}
		if (Number(options && options.trimStartSeconds) > 0 || Number(options && options.trimDurationSeconds) > 0) {
			notes.push("Trim settings affect the processed output and server preview.");
		}
		if (`${options && options.audioPreset ? options.audioPreset : "keep"}` === "mute") {
			notes.push("Output audio will be removed.");
		}
		if (options && options.createThumbnailArtifact === true && Number(options.thumbnailFrameSeconds) > 0) {
			notes.push(`Thumbnail artifact uses ${Number(options.thumbnailFrameSeconds).toFixed(2)}s into the processed output.`);
		}
		return notes;
	}

	handleContinue() {
		if (!this.props.onContinue || this.state.continueInProgress) {
			return;
		}

		const payload = this.getContextualOptions(this.state.options, this.props);
		if (this.props.allowFileRename) {
			payload.outputFileName = normalizeVideoOutputFileName(this.state.outputFileName, this.props.fileName, VIDEO_OUTPUT_EXTENSION);
		}
		if (this.props.allowOverwriteOption) {
			payload.overwriteVideo = this.state.overwriteVideo === true;
			payload.overwriteTargetPath = this.props.overwriteTargetPath || "";
		}

		this.setState({
			continueInProgress: true
		});

		let result;
		try {
			result = this.props.onContinue(payload);
		} catch (error) {
			if (this._isMounted) {
				this.setState({
					continueInProgress: false
				});
			}
			throw error;
		}

		if (this.props.closeOnContinue === true && this.props.onClose) {
			this.props.onClose();
			return;
		}

		if (result && typeof result.then === "function") {
			result.finally(() => {
				if (this._isMounted) {
					this.setState({
						continueInProgress: false
					});
				}
			});
		} else if (this._isMounted) {
			this.setState({
				continueInProgress: false
			});
		}
	}

	handleEditVideo() {
		if (this.props.onEditVideo) {
			this.props.onEditVideo({
				options: this.getContextualOptions(this.state.options, this.props),
				fileName: this.props.fileName,
				previewUrl: this.state.previewSourceUrl,
				previewFile: this.props.previewFile || null
			});
		}
	}

	handleRetryServerPreview() {
		this.clearServerPreviewTimeout();
		this.requestServerPreview(this.props, this.state.options, {
			force: true
		});
	}

	handleCancelServerPreview() {
		this.clearServerPreviewTimeout();
		this.clearServerPreviewActivityTimers();
		this.clearServerPreviewPollingTimer();
		this.serverPreviewRequestId += 1;
		this.setState({
			serverPreviewPreparingSource: false,
			serverPreviewLoading: false,
			serverPreviewError: "Preview render cancelled. Click Render Preview to try again.",
			serverPreviewCancelled: true,
			serverPreviewTimedOut: false
		});
	}

	handlePrimeServerPreview() {
		if (!this.canUseServerPreview(this.props)) {
			return;
		}

		this.setState({
			serverPreviewPrimed: true
		}, () => {
			this.clearServerPreviewTimeout();
			this.requestServerPreview(this.props, this.state.options, {
				force: true
			});
		});
	}

	handleServerPreviewAutoRenderChange(event) {
		const enabled = !!(event && event.target && event.target.checked);
		this.setState((prevState) => ({
			serverPreviewAutoRender: enabled,
			serverPreviewPrimed: enabled ? true : prevState.serverPreviewPrimed
		}));
	}

	render() {
		const {
			open = false,
			onClose = null,
			fileName = "",
			videoCount = 1,
			allowFileRename = false,
			allowOverwriteOption = false,
			overwriteLabel = "Overwrite Video",
			showThumbnailAutoLinkOption = false,
			thumbnailAutoLinkLabel = "Auto-link thumbnail to activity image",
			showEditVideoButton = true,
			title = "Advanced Video Processing",
			description = "",
			continueLabel = "Upload with Options",
			hideContinueButton = false
		} = this.props;
		const {
			options,
			overwriteVideo,
			continueInProgress,
			outputFileName,
			previewSourceUrl,
			serverPreviewAutoRender,
			serverPreviewUrl,
			serverPreviewMediaType,
			serverPreviewPreparingSource,
			serverPreviewLoading,
			serverPreviewError,
			serverPreviewUploadProgress,
			serverPreviewRequestStartedAt,
			serverPreviewElapsedSeconds,
			serverPreviewTimedOut,
			serverPreviewCancelled
		} = this.state;
		const selectionLabel = videoCount > 1
			? `${videoCount} selected videos`
			: (fileName || "selected video");
		const framingOptions = this.getAvailableFramingOptions(this.props);
		const framingHelpText = this.getFramingHelpText(this.props);
		const compactLayout = typeof window !== "undefined" && window.innerWidth < 1180;
		const canEditVideo = !!this.props.onEditVideo;
		const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 900 : 900;
		const dialogMaxHeight = Math.max(540, viewportHeight - 70);
		const previewAreaMaxHeight = Math.max(220, dialogMaxHeight - 360);
		const previewFrameDimensions = this.getPreviewFrameDimensions(options);
		const framingPresetValue = `${options && options.framingPreset ? options.framingPreset : ""}`.trim();
		const isBlurFillPreview = framingPresetValue.startsWith("blur-fill-");
		const localPreviewObjectFit = this.getPreviewObjectFit(options);
		const localPreviewTransform = this.getPreviewTransform(options);
		const localPreviewBackgroundTransform = this.getPreviewTransform(options, {
			includeBlurScale: true
		});
		const previewFilter = this.getPreviewFilter(options);
		const previewNotes = this.getPreviewNotes(options);
		const currentPreviewScrubTime = this.getCurrentPreviewScrubTime();
		const previewFrameLabel = `${Math.round(previewFrameDimensions.width)}x${Math.round(previewFrameDimensions.height)}`;
		const previewAspectRatio = (previewFrameDimensions.width > 0 && previewFrameDimensions.height > 0)
			? (previewFrameDimensions.width / previewFrameDimensions.height)
			: 16 / 9;
		const maxPreviewWidth = compactLayout
			? Math.max(260, Math.min(460, ((typeof window !== "undefined" ? window.innerWidth : 560) - 120)))
			: 720;
		let previewDisplayWidth = maxPreviewWidth;
		let previewDisplayHeight = Math.round(previewDisplayWidth / previewAspectRatio);
		if (previewDisplayHeight > previewAreaMaxHeight) {
			previewDisplayHeight = previewAreaMaxHeight;
			previewDisplayWidth = Math.round(previewDisplayHeight * previewAspectRatio);
		}
		const useServerPreview = this.canUseServerPreview(this.props, this.state);
		const usesStagedUploadServerPreview = !this.props.serverPreviewSource && !!this.props.previewFile;
		const hdrVisibilityStatus = this.getHdrVisibilityStatus(this.props, this.state);
		const shouldShowHdrToneMapping = hdrVisibilityStatus !== "sdr";
		const showServerImage = useServerPreview && serverPreviewMediaType === "image" && !!serverPreviewUrl;
		const showServerVideo = useServerPreview && serverPreviewMediaType === "video" && !!serverPreviewUrl;
		const showLocalVideo = !!previewSourceUrl && (!useServerPreview || !showServerVideo);
		const showServerPending = useServerPreview && (serverPreviewLoading === true || serverPreviewPreparingSource === true) && !serverPreviewUrl && !serverPreviewError;
		const serverPreviewElapsedLabel = serverPreviewRequestStartedAt > 0 ? this.formatServerPreviewElapsed(serverPreviewElapsedSeconds) : "";
		const showSlowServerPreviewWarning = (serverPreviewLoading || serverPreviewPreparingSource) && serverPreviewElapsedSeconds >= SERVER_PREVIEW_SLOW_WARNING_SECONDS;
		const showVerySlowServerPreviewWarning = (serverPreviewLoading || serverPreviewPreparingSource) && serverPreviewElapsedSeconds >= SERVER_PREVIEW_VERY_SLOW_WARNING_SECONDS;
		const uploadProgressPercent = Number.isFinite(serverPreviewUploadProgress)
			? Math.max(0, Math.min(100, Math.round(serverPreviewUploadProgress * 100)))
			: null;
		const serverPreviewStatusTitle = serverPreviewPreparingSource
			? "Uploading source for processed preview"
			: (serverPreviewLoading
				? "Rendering processed preview on server"
				: (serverPreviewError
					? "Processed preview needs attention"
					: ((showServerImage || showServerVideo)
						? "Processed preview ready"
						: "Processed preview idle")));
		const canCaptureTrimFromPreview = currentPreviewScrubTime !== null && (showLocalVideo || showServerVideo);
		const trimEndSeconds = Number(options.trimDurationSeconds) > 0
			? Math.round(((Number(options.trimStartSeconds) || 0) + (Number(options.trimDurationSeconds) || 0)) * 100) / 100
			: 0;

		return (
			<Modal
				open={open}
				onClose={onClose || undefined}
				center
				classNames={{ modal: "custom-modal templates" }}
				closeButton
			>
				<div style={{ width: "100%", maxWidth: "1180px", maxHeight: "calc(100vh - 60px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" }}>
					<div>
						<h3 style={{ marginTop: 0, marginBottom: "6px" }}>{title}</h3>
						<div style={{ color: "#6b7280" }}>
							{description || (
								<span>
									Apply converter options to <b>{selectionLabel}</b>. Processing settings are saved as defaults for the next video upload. File rename only applies to this upload.
								</span>
							)}
						</div>
					</div>

					<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1.2fr) minmax(360px, 1fr)", gap: "20px", alignItems: "start" }}>
						<div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
							<div style={{ color: "gray" }}>Video preview</div>
							<div style={{ position: "relative", borderRadius: "12px", overflow: "auto", background: "#111111", minHeight: compactLayout ? "240px" : "320px", maxHeight: `${previewAreaMaxHeight + 40}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
								{(previewSourceUrl || serverPreviewUrl) ? (
									<div style={{ width: "100%", maxWidth: "100%", padding: compactLayout ? "10px" : "16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
										<div
											style={{
												position: "relative",
												width: `${previewDisplayWidth}px`,
												height: `${previewDisplayHeight}px`,
												maxWidth: "100%",
												maxHeight: `${previewAreaMaxHeight}px`,
												background: "#000000",
												borderRadius: "10px",
												overflow: "hidden",
												boxShadow: "0 0 0 1px rgba(255,255,255,0.08)"
											}}
										>
											{showLocalVideo && isBlurFillPreview && (
												<video
													key={`${previewSourceUrl}-bg`}
													src={previewSourceUrl}
													preload="metadata"
													autoPlay
													muted
													loop
													playsInline
													style={{
														position: "absolute",
														inset: 0,
														width: "100%",
														height: "100%",
														objectFit: "cover",
														filter: `${previewFilter} blur(28px) brightness(0.82)`,
														transform: localPreviewBackgroundTransform,
														pointerEvents: "none"
													}}
												/>
											)}
											<div style={{ position: "absolute", inset: 0, background: isBlurFillPreview ? "linear-gradient(to top, rgba(0,0,0,0.18), rgba(0,0,0,0.04))" : "transparent", pointerEvents: "none" }} />
											{showServerImage ? (
												<img
													alt="Server rendered preview"
													src={serverPreviewUrl}
													style={{
														position: "relative",
														width: "100%",
														height: "100%",
														background: "black",
														objectFit: "contain"
													}}
												/>
											) : showServerVideo ? (
												<video
													key={serverPreviewUrl}
													ref={this.previewVideoRef}
													src={serverPreviewUrl}
													preload="metadata"
													controls
													muted
													playsInline
													style={{
														position: "relative",
														width: "100%",
														height: "100%",
														background: "black",
														objectFit: "contain"
													}}
												/>
											) : showLocalVideo ? (
												<video
													key={previewSourceUrl}
													ref={this.previewVideoRef}
													src={previewSourceUrl}
													preload="metadata"
													controls
													muted
													playsInline
													onLoadedMetadata={this.handlePreviewLoadedMetadata}
													style={{
														position: "relative",
														width: "100%",
														height: "100%",
														background: "black",
														objectFit: localPreviewObjectFit,
														filter: previewFilter,
														transform: localPreviewTransform
													}}
												/>
												) : (
													<div
														style={{
															position: "absolute",
														inset: 0,
														display: "flex",
														flexDirection: "column",
														alignItems: "center",
														justifyContent: "center",
														padding: "20px",
														textAlign: "center",
															color: "#e5e7eb",
															gap: "10px"
														}}
													>
														<div style={{ fontWeight: 600 }}>
															{showServerPending
																? (serverPreviewPreparingSource ? "Preparing server preview..." : "Rendering server preview...")
																: "Server preview unavailable"}
														</div>
														{serverPreviewError &&
															<div style={{ fontSize: "12px", lineHeight: 1.5, color: "#fecaca", maxWidth: "420px" }}>
																{serverPreviewError}
															</div>
													}
													{serverPreviewError &&
														<Button bsStyle="default" onClick={this.handleRetryServerPreview}>
															Retry Preview
															</Button>
														}
													</div>
												)}
												{useServerPreview && (serverPreviewLoading || serverPreviewPreparingSource) && (
													<div style={{ position: "absolute", inset: 0, background: "rgba(0, 0, 0, 0.45)", color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, textAlign: "center", padding: "16px" }}>
														<div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center", maxWidth: "340px" }}>
															<div>{serverPreviewPreparingSource ? "Uploading preview source..." : "Rendering server preview..."}</div>
															{serverPreviewElapsedLabel && (
																<div style={{ fontSize: "12px", fontWeight: 500, color: "#e5e7eb" }}>
																	Elapsed {serverPreviewElapsedLabel}
																</div>
															)}
															{serverPreviewPreparingSource && uploadProgressPercent !== null && (
																<div style={{ width: "220px", maxWidth: "100%" }}>
																	<div style={{ height: "8px", borderRadius: "999px", background: "rgba(255,255,255,0.2)", overflow: "hidden" }}>
																		<div style={{ width: `${uploadProgressPercent}%`, height: "100%", background: "#93c5fd" }} />
																	</div>
																	<div style={{ fontSize: "12px", fontWeight: 500, color: "#dbeafe", marginTop: "6px" }}>
																		{uploadProgressPercent}% uploaded
																	</div>
																</div>
															)}
															{showSlowServerPreviewWarning && (
																<div style={{ fontSize: "12px", fontWeight: 500, lineHeight: 1.5, color: "#fef3c7" }}>
																	Large blur-fill previews can take several minutes.
																</div>
															)}
														</div>
													</div>
												)}
											</div>
										</div>
									) : (
									<div style={{ padding: "24px", color: "#d1d5db", textAlign: "center", lineHeight: 1.5 }}>
										Select a video file to preview its framing before upload.
									</div>
								)}
							</div>
							<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
									<div style={{ fontSize: "12px", color: "#374151", background: "#f3f4f6", borderRadius: "999px", padding: "4px 10px" }}>
										Preview output: {previewFrameLabel}
									</div>
								{isBlurFillPreview &&
									<div style={{ fontSize: "12px", color: "#374151", background: "#eef6ff", borderRadius: "999px", padding: "4px 10px" }}>
										Blur-fill composition
									</div>
									}
								</div>
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.4 }}>
									The preview uses the selected source file. This shared shell is intended to be reused by the Edit Video flow as well.
									{previewNotes.length > 0 && (
										<div style={{ marginTop: "6px" }}>
											{previewNotes.map((note, index) => (
												<div key={`${note}-${index}`}>{note}</div>
											))}
										</div>
									)}
									{useServerPreview && (showServerImage || showServerVideo) && (
										<div style={{ marginTop: "6px" }}>
											Server round-trip preview is active for this video, so framing, blur-fill, tone-map checks, and playback reflect the real ffmpeg pipeline.
										</div>
									)}
									{useServerPreview && serverPreviewAutoRender !== true && (
										<div style={{ marginTop: "6px" }}>
											Auto-render is off. Adjust multiple settings first, then click Render Preview when you want a new ffmpeg render.
										</div>
									)}
									{usesStagedUploadServerPreview && (
										<div style={{ marginTop: "6px" }}>
											Processed preview temporarily stages the selected upload into a private org preview path so ffmpeg can render it. The real upload still waits for `Upload with Options`.
										</div>
									)}
									{useServerPreview && showServerPending && (
										<div style={{ marginTop: "6px" }}>
											{serverPreviewPreparingSource
												? "Uploading the selected file into the preview pipeline so ffmpeg can render it."
												: "Requesting a server-rendered preview so blur-fill, framing, and tone-map changes reflect the real ffmpeg pipeline."}
										</div>
									)}
									{useServerPreview && serverPreviewElapsedLabel && (serverPreviewLoading || serverPreviewPreparingSource) && (
										<div style={{ marginTop: "6px" }}>
											Elapsed: {serverPreviewElapsedLabel}
										</div>
									)}
									{serverPreviewError && (
										<div style={{ marginTop: "6px", color: "#b91c1c" }}>
											Server preview failed: {serverPreviewError}
										</div>
									)}
								</div>
							{showEditVideoButton && (
								<div style={{ display: "flex", justifyContent: "flex-start" }}>
									<Button onClick={this.handleEditVideo} disabled={!canEditVideo}>Edit Video</Button>
								</div>
							)}
							{showEditVideoButton && !canEditVideo && (
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.4 }}>
									This button is reserved for the reusable Edit Video flow. The shared preview shell is now in place.
								</div>
							)}
						</div>

							<div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "12px" }}>
								{useServerPreview && (
									<div style={{ border: "1px solid #dbe5f0", borderRadius: "8px", padding: "12px", background: "#f8fbff" }}>
										<div style={{ fontWeight: 600, marginBottom: "8px" }}>Processed Preview</div>
										<div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
											<Button bsStyle="default" onClick={this.handlePrimeServerPreview} disabled={serverPreviewLoading || serverPreviewPreparingSource}>
												{serverPreviewPreparingSource
													? "Preparing Preview..."
													: (serverPreviewLoading ? "Rendering Preview..." : "Render Preview")}
											</Button>
											<label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0", cursor: "pointer" }}>
												<input
													type="checkbox"
													checked={serverPreviewAutoRender === true}
													onChange={this.handleServerPreviewAutoRenderChange}
												/>
												<span>Auto-render preview</span>
											</label>
										</div>
										<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px", lineHeight: 1.5 }}>
											When auto-render is off, the current video loads immediately and processed preview only updates when you click Render Preview.
										</div>
										<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px", lineHeight: 1.5 }}>
											Large uploads with blur-fill, heavy reframing, or HDR adjustments can take several minutes to finish rendering on the server.
										</div>
										{usesStagedUploadServerPreview && (
											<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px", lineHeight: 1.5 }}>
												The first preview uploads this local source to a temporary private preview location. Final upload still waits for `Upload with Options`.
											</div>
										)}
										<div style={{ marginTop: "12px", border: "1px solid #dbe5f0", borderRadius: "8px", background: "#ffffff", padding: "10px 12px" }}>
											<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
												<div style={{ fontWeight: 600, color: serverPreviewError ? "#b91c1c" : "#111827" }}>
													{serverPreviewStatusTitle}
												</div>
												{serverPreviewElapsedLabel && (serverPreviewLoading || serverPreviewPreparingSource) && (
													<div style={{ fontSize: "12px", color: "#6b7280" }}>
														Elapsed {serverPreviewElapsedLabel}
													</div>
												)}
											</div>
											{serverPreviewPreparingSource && uploadProgressPercent !== null && (
												<div style={{ marginTop: "8px" }}>
													<div style={{ height: "8px", borderRadius: "999px", background: "#e5e7eb", overflow: "hidden" }}>
														<div style={{ width: `${uploadProgressPercent}%`, height: "100%", background: "#60a5fa" }} />
													</div>
													<div style={{ marginTop: "6px", fontSize: "12px", color: "#4b5563" }}>
														{uploadProgressPercent}% uploaded to the temporary preview source
													</div>
												</div>
											)}
											{showSlowServerPreviewWarning && (
												<div style={{ marginTop: "8px", fontSize: "12px", lineHeight: 1.5, color: "#92400e" }}>
													The server is still working. This is expected for full-length blur-fill previews.
												</div>
											)}
											{showVerySlowServerPreviewWarning && !serverPreviewTimedOut && (
												<div style={{ marginTop: "6px", fontSize: "12px", lineHeight: 1.5, color: "#92400e" }}>
													If this keeps running past about 15 minutes, the modal will stop waiting. Retry Preview will first look for any finished render before starting over.
												</div>
											)}
											{serverPreviewCancelled && !serverPreviewLoading && !serverPreviewPreparingSource && (
												<div style={{ marginTop: "8px", fontSize: "12px", lineHeight: 1.5, color: "#6b7280" }}>
													This window stopped waiting for the last request. You can render again when ready.
												</div>
											)}
											{serverPreviewError && (
												<div style={{ marginTop: "8px", fontSize: "12px", lineHeight: 1.5, color: "#b91c1c" }}>
													{serverPreviewError}
												</div>
											)}
											{(serverPreviewLoading || serverPreviewPreparingSource) && (
												<div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
													<Button bsStyle="default" onClick={this.handleCancelServerPreview}>
														Stop Waiting
													</Button>
												</div>
											)}
											{serverPreviewError && !serverPreviewLoading && !serverPreviewPreparingSource && (
												<div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
													<Button bsStyle="default" onClick={this.handleRetryServerPreview}>
														Retry Preview
													</Button>
												</div>
											)}
										</div>
									</div>
								)}

							{allowFileRename &&
								<div>
									<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Video Name</label>
									<input
										className="form-control"
										type="text"
										value={outputFileName}
										onChange={this.handleOutputFileNameChange}
										disabled={overwriteVideo}
									/>
									<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
										This video will be saved as .{VIDEO_OUTPUT_EXTENSION}. If you type a different extension, it will be replaced.
									</div>
									{overwriteVideo && (
										<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
											Overwrite keeps the current Drive video path and replaces the playback file when processing finishes.
										</div>
									)}
								</div>
							}

							{allowOverwriteOption && (
								<label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0", cursor: "pointer" }}>
									<input
										type="checkbox"
										checked={overwriteVideo}
										onChange={this.handleOverwriteVideoChange}
									/>
									<span style={{ fontWeight: 600 }}>{overwriteLabel}</span>
								</label>
							)}

							{shouldShowHdrToneMapping && (
								<div>
									<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>HDR Tone Mapping</label>
									<select
										className="form-control"
										name="hdrToneMapPreset"
										value={options.hdrToneMapPreset}
										onChange={this.handleSelectChange}
									>
										{VIDEO_TONE_MAP_PRESET_OPTIONS.map((option) => (
											<option key={option.value} value={option.value}>{option.label}</option>
										))}
									</select>
									<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
										Applies only when the uploaded source appears to be HDR.
									</div>
								</div>
							)}
							{hdrVisibilityStatus === "sdr" && (
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px" }}>
									HDR tone mapping is hidden for this Drive video because the current source probe does not appear to be HDR.
								</div>
							)}

							<div>
								<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Resize</label>
								<select
									className="form-control"
									name="resizePreset"
									value={options.resizePreset}
									onChange={this.handleSelectChange}
								>
									{VIDEO_RESIZE_PRESET_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>{option.label}</option>
									))}
								</select>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
									Use this for simple downscale targets. Layout presets below manage their own output size.
								</div>
							</div>

							<div>
								<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Style Filter</label>
								<select
									className="form-control"
									name="styleFilterPreset"
									value={options.styleFilterPreset}
									onChange={this.handleSelectChange}
								>
									{VIDEO_STYLE_FILTER_PRESET_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>{option.label}</option>
									))}
								</select>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
									Simple look presets that apply to both preview and final conversion output.
								</div>
							</div>

							<div>
								<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Layout & Framing</label>
								<select
									className="form-control"
									name="framingPreset"
									value={options.framingPreset}
									onChange={this.handleSelectChange}
								>
									{framingOptions.map((option) => (
										<option key={option.value} value={option.value}>{option.label}</option>
									))}
								</select>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
									{framingHelpText}
								</div>
							</div>

							<div>
								<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Rotate / Flip</label>
								<select
									className="form-control"
									name="rotationPreset"
									value={options.rotationPreset}
									onChange={this.handleSelectChange}
								>
									{VIDEO_ROTATION_PRESET_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>{option.label}</option>
									))}
								</select>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
									Applied before resize and framing so orientation changes flow through the rest of the pipeline.
								</div>
							</div>

							<div>
								<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Audio</label>
								<select
									className="form-control"
									name="audioPreset"
									value={options.audioPreset}
									onChange={this.handleSelectChange}
								>
									{VIDEO_AUDIO_PRESET_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>{option.label}</option>
									))}
								</select>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
									Use mute when you want a clean visual loop without carrying the source audio forward.
								</div>
							</div>

							<div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px" }}>
								<div style={{ fontWeight: 600, marginBottom: "10px" }}>Trim</div>
								<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "12px" }}>
									<div>
										<label style={{ fontWeight: 500, marginBottom: "6px", display: "block" }}>Start At (seconds)</label>
										<input
											className="form-control"
											type="number"
											min="0"
											step="0.1"
											name="trimStartSeconds"
											value={options.trimStartSeconds}
											onChange={this.handleNumberOptionChange}
										/>
										<div style={{ marginTop: "8px" }}>
											<Button
												bsSize="small"
												bsStyle="default"
												onClick={this.handleCaptureTrimStartFromPreview}
												disabled={!canCaptureTrimFromPreview}
											>
												Mark Start from Scrub
											</Button>
										</div>
									</div>
									<div>
										<label style={{ fontWeight: 500, marginBottom: "6px", display: "block" }}>Duration (seconds)</label>
										<input
											className="form-control"
											type="number"
											min="0"
											step="0.1"
											name="trimDurationSeconds"
											value={options.trimDurationSeconds}
											onChange={this.handleNumberOptionChange}
										/>
										<div style={{ marginTop: "8px" }}>
											<Button
												bsSize="small"
												bsStyle="default"
												onClick={this.handleCaptureTrimEndFromPreview}
												disabled={!canCaptureTrimFromPreview}
											>
												Mark End from Scrub
											</Button>
										</div>
									</div>
								</div>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px" }}>
									Leave duration at 0 to keep everything from the chosen start point through the end of the video.
								</div>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px" }}>
									Current scrub position: {currentPreviewScrubTime !== null ? `${currentPreviewScrubTime.toFixed(2)}s` : "unavailable until the preview video is loaded"}
									{trimEndSeconds > 0 ? ` | Trim end: ${trimEndSeconds.toFixed(2)}s` : ""}
								</div>
							</div>

							<div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px" }}>
								<label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0", cursor: "pointer" }}>
									<input
										type="checkbox"
										name="createThumbnailArtifact"
										checked={options.createThumbnailArtifact}
										onChange={this.handleCheckboxChange}
									/>
									<span style={{ fontWeight: 600 }}>Create JPEG thumbnail Drive artifact</span>
								</label>
								<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px" }}>
									Generates a sibling Drive image file after the video conversion completes. Defaults to the first frame unless you choose a later timestamp below.
								</div>
								{options.createThumbnailArtifact === true && (
									<div style={{ marginTop: "12px" }}>
										<label style={{ fontWeight: 500, marginBottom: "6px", display: "block" }}>Thumbnail Frame Time (seconds)</label>
										<input
											className="form-control"
											type="number"
											min="0"
											step="0.1"
											name="thumbnailFrameSeconds"
											value={options.thumbnailFrameSeconds}
											onChange={this.handleNumberOptionChange}
										/>
										<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px" }}>
											For trimmed videos, this timestamp is relative to the processed output, not the original source start.
										</div>
									</div>
								)}
								{showThumbnailAutoLinkOption && options.createThumbnailArtifact === true && (
									<div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
										<label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0", cursor: "pointer" }}>
											<input
												type="checkbox"
												name="autoLinkThumbnailArtifact"
												checked={options.autoLinkThumbnailArtifact === true}
												onChange={this.handleCheckboxChange}
											/>
											<span style={{ fontWeight: 600 }}>{thumbnailAutoLinkLabel}</span>
										</label>
										<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px" }}>
											When processing finishes, update the activity image reference to point at the generated thumbnail automatically.
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.4 }}>
						Output format is currently fixed to MP4 (H.264 video + AAC audio) because that is the safest target for iOS, Android, and mobile web playback.
					</div>

					<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.4 }}>
						Center crop, pad, trim, rotate, mute, and style-filter options now flow through the same shared upload and Edit Video pipeline.
					</div>

					<div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
						<Button onClick={onClose || undefined} disabled={continueInProgress}>Cancel</Button>
						{!hideContinueButton &&
							<Button bsStyle="primary" onClick={this.handleContinue} disabled={continueInProgress}>
								{continueInProgress ? "Saving..." : continueLabel}
							</Button>
						}
					</div>
				</div>
			</Modal>
		);
	}
}

export default VideoProcessingOptionsModal;
